// A katalógus-kezelők fa-vezetővonalainak számítása (kiadás-kategóriák, eszköz-kategóriák) — tiszta függvény,
// ezért külön fájl: a rajzoló komponens (`catalog-manager.tsx`) csak komponenseket exportál (Fast Refresh).

/**
 * Egy vezetővonal-oszlop alakja EGY soron.
 *
 * `blank` — az a csoport már LEZÁRULT feljebb, tehát nincs szár (ez a tétel, amit a felület sokáig hibásan
 * `line`-ként rajzolt: a fa alja alatt egy vonal lógott, ami semmihez nem vezetett).
 */
export type TreeGuide =
  /** nincs szár: az ehhez a szinthez tartozó ős volt az utolsó testvére, a csoport lezárult */
  | 'blank'
  /** az ős csoportja MÉG TART: a szár végigfut a soron */
  | 'line'
  /** SAJÁT oszlop, van még testvér: végigfutó szár + könyök */
  | 'tee'
  /** SAJÁT oszlop, ez az UTOLSÓ testvér: a szár a sor közepén véget ér + könyök */
  | 'elbow';

/**
 * A fa-vezetővonalak soronkénti, szintenkénti alakja — egy menetben az EGÉSZ listára.
 *
 * ⚠️ A KULCS-SZABÁLY, amit kétszer rontottam el: **a `level` indexű oszlop a `level + 1` MÉLYSÉGŰ
 * testvér-csoporté**, nem a `level` mélységűé. Az eltolás oka látható: a GYÖKÉR-soron egyáltalán nincs
 * vezetővonal (0 mélység = 0 oszlop), tehát nincs is mit folytatni a gyökér szintjén — az első oszlop már a
 * MÁSODIK szint csoportját hordozza. Egy sor `d` oszlopából az utolsó (`d - 1`) a SAJÁT csoportja.
 *
 * Ettől a szabály EGYSÉGES: minden oszlop ugyanazt kérdezi — „tart-e még az a csoport a sor ALATT?" —, és
 * csak a rajz tér el (az utolsó oszlop könyököt is kap).
 *
 * A lista sorrendje MÉLYSÉG-ELSŐ (a BFF adja: minden csomópont után azonnal a gyerekei) — erre épít
 * mindkét menet.
 *
 * KÉT MÉRT HIBA, amit ez a függvény zár (mindkettő user-lelet, élesben):
 *   1. az ős-oszlopok szára FELTÉTEL NÉLKÜL végigfutott → az utolsó ág alatt árva vonal lógott;
 *   2. a javítás a `level` mélységű ősre nézett → az UTOLSÓ főkategória alatt MINDEN vonal eltűnt, nem csak
 *      az utolsó alkategória alatti.
 */
export function treeGuides<T>(rows: readonly T[], depthOf: (row: T) => number): TreeGuide[][] {
  const depths = rows.map(depthOf);

  // 1. menet HÁTULRÓL: utolsó testvér-e az adott sor. A `seen[d]` azt jelenti, hogy a sor ALATT — még a
  // saját csoportján belül — láttunk már azonos mélységűt. Egy SEKÉLYEBB sor lezárja a nála mélyebb
  // szinteket (a `seen` törlése), ezért a „testvér" soha nem szivárog át egy másik szülő gyerekei közül.
  const lastOf = new Array<boolean>(rows.length).fill(true);
  const seen: boolean[] = [];
  for (let i = rows.length - 1; i >= 0; i--) {
    const d = depths[i] ?? 0;
    lastOf[i] = seen[d] !== true;
    seen[d] = true;
    for (let k = d + 1; k < seen.length; k++) seen[k] = false;
  }

  // 2. menet ELÖLRŐL: a szintenkénti ŐS sorindexe egy veremből (mélység-első listán a legutóbbi, az adott
  // mélységű sor). Az oszlop GAZDÁJA a `level + 1` mélységű ős — a saját oszlopé maga a sor.
  const ancestor: number[] = [];
  return rows.map((_, i) => {
    const d = depths[i] ?? 0;
    ancestor.length = d;
    const cols: TreeGuide[] = [];
    for (let level = 0; level < d; level++) {
      const own = level === d - 1;
      const owner = own ? i : ancestor[level + 1];
      const groupContinues = owner !== undefined && lastOf[owner] !== true;
      if (own) cols.push(groupContinues ? 'tee' : 'elbow');
      else cols.push(groupContinues ? 'line' : 'blank');
    }
    ancestor[d] = i;
    return cols;
  });
}
